'use client';

/**
 * Store Provider Module
 *
 * This module implements the StoreProvider component, which is responsible for:
 * - Initializing global store state on app load
 * - Fetching user data from APIs when a user logs in
 * - Fetching manager-specific data when viewing manager dashboards
 * - Managing user consent preferences
 * - Handling errors during data fetching and providing user notifications
 *
 * The provider uses React hooks (useEffect, useRef, useState) to manage side effects
 * and a Zustand store to maintain global application state. It implements caching
 * strategies to prevent redundant API calls.
 */

import { useApiHealthCheck } from './hooks/useApiHealthCheck';
import { useAuth } from '@/components/Auth';
import { useInitializeAppConfig } from './hooks/useInitializeAppConfig';
import { useInitializeConsentState } from './hooks/useInitializeConsentState';
import { useInitializeManagerStoreData } from './hooks/useInitializeManagerStoreData';
import { useInitializeUserStoreData } from './hooks/useInitializeUserStoreData';
import React, { useEffect, useState } from 'react';

/**
 * Props for StoreProvider component
 *
 * @interface StoreProviderProps
 * @property {React.ReactNode} children - The child components to be wrapped by this provider.
 *                                        These children have access to the global store state.
 */
interface StoreProviderProps {
  children: React.ReactNode;
}

/**
 * Store Initializer Component
 *
 * This internal component runs side effects that hydrate the global Zustand store.
 * It does not render UI and exists only to:
 * - load consent from session storage,
 * - fetch manager-specific datasets when on manager routes,
 * - fetch the signed-in user's datasets,
 * - map API responses into frontend models,
 * - publish notifications and errors.
 */
const StoreInitializer: React.FC = () => {
  // Authenticated user context (null when unauthenticated).
  const { user } = useAuth();

  // Hooks for Initializing Store Data
  useApiHealthCheck(); // API health status
  useInitializeAppConfig(); // App config
  useInitializeConsentState(); // Consent state
  useInitializeManagerStoreData({ user }); // Manager-specific data
  useInitializeUserStoreData({ user }); // User data

  // This component does not render any UI; it only initializes store data.
  return null;
};

/**
 * Store Provider Component
 *
 * This component can be used to initialize the store with data from APIs
 * or perform other setup tasks when the app loads.
 */
export const StoreProvider: React.FC<StoreProviderProps> = ({ children }) => {
  // Tracks hydration boundary so initializer only runs on client.
  const [isClient, setIsClient] = useState(false);

  // Ensure this only runs on the client side
  useEffect(() => {
    setIsClient(true);
  }, []);

  // Return children without store initialization during SSR
  if (!isClient) {
    return <>{children}</>;
  }

  return (
    <>
      <StoreInitializer />
      {children}
    </>
  );
};
